import { AuthResponse, MccsRoles } from '@/@types/generated'
import { useAuth } from '@/hooks/useAuth'
import { Feature } from '@/lib/features/feature'
import React from 'react'

export function hasRole(
  role: MccsRoles | MccsRoles[],
  user: Pick<AuthResponse, 'roles'> | null,
  hasNone = false
): boolean {
  const roles = Array.isArray(role) ? role : [role]

  // No user, no access
  if (!user) return false

  // Check if user has an administrator role
  if (user.roles?.includes(MccsRoles.ADMINISTRATOR)) return true

  if (hasNone)
    // Check if user does not have any of the specified roles
    return roles.every((r) => !user.roles?.includes(r))
  else
    // Check if user has at least one of the required roles
    return roles.some((r) => user.roles?.includes(r))

  // Otherwise, no access
  return false
}

interface HasRoleProps {
  role: MccsRoles | MccsRoles[]
  noRoleComponent?: React.ReactNode
}

export function HasRole({
  role,
  noRoleComponent,
  children,
}: React.PropsWithChildren<HasRoleProps>) {
  const { user } = useAuth()

  // No access
  if (!hasRole(role, user)) return noRoleComponent || null

  // Access
  return <>{children}</>
}

export function hasFeature(
  feature: Feature | Feature[],
  user: AuthResponse | null
): boolean {
  const features = Array.isArray(feature) ? feature : [feature]

  // No user, no access
  if (!user) return false

  // Check if user has at least one of the required features
  if (features.some((f) => f.isEnabled(user))) return true

  // Otherwise, no access
  return false
}

interface HasFeatureProps {
  feature: Feature | Feature[]
  noFeatureComponent?: React.ReactNode
}

export function HasFeature({
  feature,
  noFeatureComponent,
  children,
}: React.PropsWithChildren<HasFeatureProps>) {
  const { user } = useAuth()

  // No feature
  if (!hasFeature(feature, user)) return noFeatureComponent || null

  // Has feature
  return <>{children}</>
}
